import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

const Checkbox = ({ state = 'empty' }) => (
  <span
    className={`w-4 h-4 rounded-sm border flex items-center justify-center shrink-0 ${
      state === 'empty' ? 'bg-[#f9fafa] border-[#616a79]' : 'bg-[#0b7667] border-[#0b7667]'
    }`}
  >
    {state === 'checked' && <span className="material-icons text-[12px] text-[#f9fafa] leading-none">check</span>}
    {state === 'indeterminate' && <span className="material-icons text-[12px] text-[#f9fafa] leading-none">remove</span>}
  </span>
)

/**
 * "Columns" trigger + a right-side "Select Columns" panel (search + select-all
 * + per-column checkboxes). `columns` is the full list of toggleable column
 * defs ({ key, label }); `visibleKeys` is the currently-shown subset; onChange
 * fires immediately on every toggle — there's no separate Apply step, columns
 * show/hide live in the table as you check/uncheck them.
 *
 * The panel is portaled to document.body: this page's layout has a
 * transformed ancestor somewhere above the trigger, which would otherwise
 * make `position: fixed` resolve against that ancestor instead of the
 * viewport and get painted under the table's sticky header / app top bar.
 */
const ColumnSelector = ({ columns, visibleKeys, onChange }) => {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const triggerRef = useRef(null)
  const panelRef = useRef(null)

  useEffect(() => {
    if (!open) return
    const handleClickOutside = (e) => {
      if (triggerRef.current?.contains(e.target)) return
      if (panelRef.current?.contains(e.target)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  const filtered = columns.filter((c) => c.label.toLowerCase().includes(search.toLowerCase()))
  const allChecked = columns.length > 0 && columns.every((c) => visibleKeys.includes(c.key))
  const noneChecked = columns.every((c) => !visibleKeys.includes(c.key))
  const selectAllState = allChecked ? 'checked' : noneChecked ? 'empty' : 'indeterminate'

  const toggleAll = () => onChange(allChecked ? [] : columns.map((c) => c.key))
  const toggleOne = (key) => onChange(visibleKeys.includes(key) ? visibleKeys.filter((k) => k !== key) : [...visibleKeys, key])

  return (
    <div ref={triggerRef}>
      <button
        className="flex items-center gap-2 h-10 px-3 rounded border border-[#dfe4ed] bg-[#f2f4f7] cursor-pointer"
        onClick={() => setOpen((o) => !o)}
      >
        <span className="material-icons text-base text-[#272d37]">view_column</span>
        <span className="text-sm font-medium text-[#272d37]">Columns</span>
        {/* <span className="material-icons text-base text-[#272d37]">expand_more</span> */}
      </button>

      {open &&
        createPortal(
          <div ref={panelRef} className="fixed right-0 top-0 h-full w-[380px] bg-[#f9fafa] shadow-[0px_4px_32px_0px_rgba(22,28,39,0.1)] z-[1000] flex flex-col">
            <div className="flex items-center gap-4 border-b border-[#dfe4ed] px-4 py-2 shrink-0">
              <span className="flex-1 text-sm font-semibold text-[#272d37]">Select Columns</span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="flex items-center justify-center p-2 rounded-full border-none bg-transparent cursor-pointer hover:bg-[#f2f4f7]"
              >
                <span className="material-icons text-xl text-[#272d37]">close</span>
              </button>
            </div>

            <div className="flex flex-col gap-2 p-4 flex-1 min-h-0">
              <div className="flex items-center gap-2 bg-[#f9fafa] border border-[#b1bbcb] rounded px-2 py-2.5 shrink-0">
                <span className="material-icons text-xl text-[#616a79]">search</span>
                <input
                  type="text"
                  placeholder="Search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="flex-1 text-sm text-[#272d37] placeholder:text-[#97a1b4] outline-none border-none bg-transparent min-w-0"
                />
              </div>

              <div className="border border-[#e5e9f0] rounded flex-1 overflow-y-auto flex flex-col">
                <button
                  type="button"
                  onClick={toggleAll}
                  className="flex items-center gap-2 h-9 px-4 border-b border-[#dfe4ed] bg-[#f9fafa] border-none cursor-pointer text-left shrink-0"
                >
                  <Checkbox state={selectAllState} />
                  <span className="flex-1 text-sm font-medium text-[#272d37]">Select all</span>
                  <span className="text-sm text-[#272d37] whitespace-nowrap">{visibleKeys.length}/{columns.length} selected</span>
                </button>
                {filtered.map((col) => (
                  <button
                    key={col.key}
                    type="button"
                    onClick={() => toggleOne(col.key)}
                    className="flex items-center gap-2 h-9 px-4 bg-[#f9fafa] border-none cursor-pointer text-left shrink-0"
                  >
                    <Checkbox state={visibleKeys.includes(col.key) ? 'checked' : 'empty'} />
                    <span className="text-sm text-[#272d37]">{col.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  )
}

export default ColumnSelector
